<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" :class="{ 'map-modal': isFullscreen }" @click="handleCancel">
      <div class="modal-dialog" @click.stop>
        <div class="modal-header">
          <h3>Create New Sector</h3>
          <button class="close-btn" @click="handleCancel">
            <i class="fa fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <!-- Sector Name -->
          <div class="form-group">
            <label for="sector-name">Sector Name:</label>
            <input
              id="sector-name"
              type="text"
              class="form-control"
              v-model="sectorName"
              placeholder="Enter sector name"
              @keyup.enter="handleConfirm"
              @keyup.esc="handleCancel"
              ref="nameInput"
            >
          </div>
          
          <!-- Zone Selection -->
          <div class="form-group">
            <label for="zone-select">Select Zone:</label>
            <select
              id="zone-select"
              class="form-control"
              v-model="selectedZoneId"
              @change="handleZoneChange"
            >
              <option value="">-- Select a zone --</option>
              <option
                v-for="zone in zones"
                :key="zone.id"
                :value="zone.id"
              >
                {{ zone.name }}
              </option>
              <option value="new">+ Create New Zone</option>
            </select>
          </div>
          
          <!-- New Zone Name (if creating new) -->
          <div v-if="selectedZoneId === 'new'" class="form-group">
            <label for="new-zone-name">New Zone Name:</label>
            <input
              id="new-zone-name"
              type="text"
              class="form-control"
              v-model="newZoneName"
              placeholder="Enter new zone name"
              @keyup.enter="handleConfirm"
              @keyup.esc="handleCancel"
            >
          </div>

          <!-- Location Selection (when creating new zone) -->
          <div v-if="selectedZoneId === 'new'" class="form-group">
            <label for="location-select">Select Location:</label>
            <select
              id="location-select"
              class="form-control"
              v-model="selectedLocationId"
            >
              <option value="">-- Select a location --</option>
              <option
                v-for="location in schoolLocations"
                :key="location.id"
                :value="location.id"
              >
                {{ location.name }}
              </option>
              <option value="new">+ Create New Location</option>
            </select>
          </div>

          <!-- New Location Fields (if creating new location) -->
          <div v-if="selectedZoneId === 'new' && selectedLocationId === 'new'">
            <div class="form-group">
              <label for="new-location-name">Location Name:</label>
              <input
                id="new-location-name"
                type="text"
                class="form-control"
                v-model="newLocationName"
                placeholder="Enter location name"
                @keyup.enter="handleConfirm"
                @keyup.esc="handleCancel"
              >
            </div>

            <div class="form-group">
              <label for="address-street">Street Address:</label>
              <input
                id="address-street"
                type="text"
                class="form-control"
                v-model="address.street"
                placeholder="Enter street address"
                @keyup.enter="handleConfirm"
                @keyup.esc="handleCancel"
              >
            </div>

            <div class="form-group">
              <label for="address-city">City:</label>
              <input
                id="address-city"
                type="text"
                class="form-control"
                v-model="address.city"
                placeholder="Enter city"
                @keyup.enter="handleConfirm"
                @keyup.esc="handleCancel"
              >
            </div>

            <div class="form-group">
              <label for="address-state">State:</label>
              <select
                id="address-state"
                class="form-control"
                v-model="address.state"
              >
                <option value="">-- Select a state --</option>
                <option
                  v-for="state in usStates"
                  :key="state.code"
                  :value="state.code"
                >
                  {{ state.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="address-zip">Zip Code:</label>
              <input
                id="address-zip"
                type="text"
                class="form-control"
                v-model="address.zip"
                placeholder="Enter zip code"
                @keyup.enter="handleConfirm"
                @keyup.esc="handleCancel"
              >
            </div>
          </div>

          <!-- Validation Message -->
          <div v-if="validationMessage" class="validation-message">
            <i class="fa fa-exclamation-triangle"></i>
            {{ validationMessage }}
          </div>
        </div>
        
        <div class="modal-footer">
          <button class="btn btn-primary" @click="handleConfirm" :disabled="!isValid">
            <i class="fa fa-check"></i> OK
          </button>
          <button class="btn btn-default" @click="handleCancel">
            <i class="fa fa-times"></i> Cancel
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'NewSectorDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    zones: {
      type: Array,
      default: () => []
    },
    isFullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sectorName: '',
      selectedZoneId: '',
      newZoneName: '',
      selectedLocationId: '',
      newLocationName: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      validationMessage: '',
      // US States for dropdown
      usStates: [
        { code: 'AL', name: 'Alabama' },
        { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' },
        { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' },
        { code: 'DE', name: 'Delaware' },
        { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' },
        { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' },
        { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' },
        { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' },
        { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' },
        { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' },
        { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' },
        { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' },
        { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' },
        { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' },
        { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' },
        { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' },
        { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' },
        { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' },
        { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' }
      ]
    };
  },
  computed: {
    ...mapGetters('serviceAreas', ['schoolLocations']),

    selectedZone() {
      if (this.selectedZoneId && this.selectedZoneId !== 'new') {
        return this.zones.find(z => z.id === parseInt(this.selectedZoneId));
      }
      return null;
    },

    isCreatingNewZone() {
      return this.selectedZoneId === 'new';
    },

    isCreatingNewLocation() {
      return this.selectedLocationId === 'new';
    },

    isValid() {
      // Validate sector name
      if (!this.sectorName.trim()) {
        return false;
      }

      // Validate zone selection
      if (!this.selectedZoneId) {
        return false;
      }

      // If creating new zone
      if (this.isCreatingNewZone) {
        // Validate zone name
        if (!this.newZoneName.trim()) {
          return false;
        }

        // Validate location selection
        if (!this.selectedLocationId) {
          return false;
        }

        // If creating new location, validate location fields
        if (this.isCreatingNewLocation) {
          if (!this.newLocationName.trim()) {
            return false;
          }
          if (!this.address.street.trim()) {
            return false;
          }
          if (!this.address.city.trim()) {
            return false;
          }
          if (!this.address.state) {
            return false;
          }
          if (!this.address.zip.trim()) {
            return false;
          }
        }
      }

      return true;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset form when showing
        this.resetForm();
        // Focus name input after DOM update
        this.$nextTick(() => {
          if (this.$refs.nameInput) {
            this.$refs.nameInput.focus();
          }
        });
      }
    },
    sectorName() {
      this.clearValidation();
    },
    selectedZoneId() {
      this.clearValidation();
      // Reset location selection when zone changes
      if (this.selectedZoneId !== 'new') {
        this.selectedLocationId = '';
        this.newLocationName = '';
        this.address = {
          street: '',
          city: '',
          state: '',
          zip: ''
        };
      }
    },
    newZoneName() {
      this.clearValidation();
    },
    selectedLocationId() {
      this.clearValidation();
      // Reset location fields when selection changes
      if (this.selectedLocationId !== 'new') {
        this.newLocationName = '';
        this.address = {
          street: '',
          city: '',
          state: '',
          zip: ''
        };
      }
    }
  },
  methods: {
    handleZoneChange() {
      // Clear new zone name if not creating new zone
      if (this.selectedZoneId !== 'new') {
        this.newZoneName = '';
      }
    },
    handleConfirm() {
      if (!this.isValid) {
        this.showValidation();
        return;
      }

      // Prepare data to emit
      const data = {
        name: this.sectorName.trim(),
        zone_id: this.selectedZoneId === 'new' ? 0 : parseInt(this.selectedZoneId),
        zone_name: this.selectedZoneId === 'new' ? this.newZoneName.trim() : null
      };

      // Add location data based on zone selection
      if (this.selectedZoneId === 'new') {
        // Creating new zone - need to specify location
        if (this.selectedLocationId === 'new') {
          // Creating new location
          data.location_id = 0;
          data.location_name = this.newLocationName.trim();
          data.address = {
            street: this.address.street.trim(),
            city: this.address.city.trim(),
            state: this.address.state,
            zip: this.address.zip.trim()
          };
        } else {
          // Using existing location
          data.location_id = parseInt(this.selectedLocationId);
        }
      } else {
        // Using existing zone - get location_id from zone
        const zone = this.zones.find(z => z.id === parseInt(this.selectedZoneId));
        if (zone && zone.location_id) {
          data.location_id = zone.location_id;
        }
      }

      this.$emit('confirm', data);
      this.resetForm();
    },
    handleCancel() {
      this.$emit('cancel');
      this.resetForm();
    },
    resetForm() {
      this.sectorName = '';
      this.selectedZoneId = '';
      this.newZoneName = '';
      this.selectedLocationId = '';
      this.newLocationName = '';
      this.address = {
        street: '',
        city: '',
        state: '',
        zip: ''
      };
      this.validationMessage = '';
    },
    showValidation() {
      if (!this.sectorName.trim()) {
        this.validationMessage = 'Please enter a sector name';
      } else if (!this.selectedZoneId) {
        this.validationMessage = 'Please select a zone';
      } else if (this.selectedZoneId === 'new') {
        if (!this.newZoneName.trim()) {
          this.validationMessage = 'Please enter a new zone name';
        } else if (!this.selectedLocationId) {
          this.validationMessage = 'Please select a location';
        } else if (this.selectedLocationId === 'new') {
          if (!this.newLocationName.trim()) {
            this.validationMessage = 'Please enter a location name';
          } else if (!this.address.street.trim()) {
            this.validationMessage = 'Please enter a street address';
          } else if (!this.address.city.trim()) {
            this.validationMessage = 'Please enter a city';
          } else if (!this.address.state) {
            this.validationMessage = 'Please select a state';
          } else if (!this.address.zip.trim()) {
            this.validationMessage = 'Please enter a zip code';
          }
        }
      }
    },
    clearValidation() {
      this.validationMessage = '';
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* When inside the map container (for fullscreen support) */
.modal-overlay.map-modal {
  position: absolute;
  z-index: 2000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e5e5;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3388ff;
  box-shadow: 0 0 0 3px rgba(51, 136, 255, 0.1);
}

select.form-control {
  cursor: pointer;
}

.validation-message {
  color: #d9534f;
  font-size: 13px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.validation-message i {
  font-size: 14px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #3388ff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2c78e8;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-default {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.btn-default:hover {
  background: #e8e8e8;
}

/* Modal animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-dialog,
.modal-fade-leave-active .modal-dialog {
  transition: transform 0.3s;
}

.modal-fade-enter .modal-dialog,
.modal-fade-leave-to .modal-dialog {
  transform: scale(0.9);
}
</style>